import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Select, Spin, Empty } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { USERGUIDE_URL } from "../../Constant/AppConstants";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { useIsMobile, useIsTablet } from "../../Hook";
import { drawerTitle } from "../AppUi";

interface UserGuideDrawerProps {
  visible: boolean;
  onClose: () => void;
  featureLabel: string;
  navURL: string;
  youtubeUrl?: string;
}

function getReadingTime(text: string): number {
  const wordsPerMinute = 200;
  const numberOfWords = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(numberOfWords / wordsPerMinute));
}

export const UserGuideDrawer: React.FC<UserGuideDrawerProps> = ({
  visible,
  onClose,
  featureLabel,
  navURL,
  youtubeUrl,
}) => {
  const [selectedOption, setSelectedOption] = useState<
    "english" | "sinhala" | "youtube"
  >("english");
  const [loading, setLoading] = useState<boolean>(false);
  const [content, setContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Helper to get youtube embed url
  const embedUrl = useMemo(() => {
    if (!youtubeUrl) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = youtubeUrl.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return null;
  }, [youtubeUrl]);

  useEffect(() => {
    if (visible) {
      setSelectedOption("english");
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setContent("");
      setError(null);
      return;
    }

    if (selectedOption === "youtube") {
      setContent("");
      setError(null);
      return;
    }

    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        const fileUrl = USERGUIDE_URL
          ? `${USERGUIDE_URL}/${selectedOption}/${navURL}.md`
          : `/userguides/${selectedOption}/${navURL}.md`;

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to load document (${response.status})`);
        }
        const text = await response.text();
        if (
          text.trim().toLowerCase().startsWith("<!doctype html") ||
          text.trim().toLowerCase().startsWith("<html")
        ) {
          throw new Error("Document not found (SPA routing fallback)");
        }
        setContent(text);
      } catch (err: any) {
        console.error("Error fetching user guide:", err);
        setError(
          `The user guide for "${featureLabel}" is not available in ${
            selectedOption === "english" ? "English" : "Sinhala"
          } yet.`,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [selectedOption, navURL, visible, featureLabel]);

  const readingTime = useMemo(
    () => (content ? getReadingTime(content) : 0),
    [content],
  );

  return (
    <Drawer
      title={drawerTitle(` ${featureLabel} Guide`)}
      placement="left"
      width={isMobile || isTablet ? "100%" : "50%"}
      onClose={onClose}
      open={visible}
      bodyStyle={{ padding: "24px 32px" }}
      headerStyle={{
        borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
        padding: "14px 24px",
      }}
      className="dark:bg-slate-900"
    >
      <Spin spinning={loading} size="large">
        <div className="flex items-center justify-between gap-3">
          {readingTime > 0 && !loading && !error && (
            <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 dark:bg-slate-800 text-[11px] font-medium text-slate-500 dark:text-slate-400">
              <ClockCircleOutlined className="text-[10px]" />
              {readingTime} min read
            </span>
          )}
          <Select
            value={selectedOption}
            onChange={(val) => setSelectedOption(val)}
            style={{ width: 190 }}
            dropdownStyle={{ borderRadius: "8px" }}
            className="text-xs"
            options={[
              { value: "english", label: "🇬🇧 English Guide" },
              { value: "sinhala", label: "🇱🇰 Sinhala Guide (සිංහල)" },
              {
                value: "youtube",
                label: "🎥 YouTube Tutorial",
                disabled: true,
              },
            ]}
          />
        </div>

        <div className="pr-1 md-doc-container">
          {selectedOption === "youtube" ? (
            <div className="flex flex-col items-center justify-center py-8">
              {embedUrl ? (
                <div className="w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800 bg-black">
                  <iframe
                    width="100%"
                    height="100%"
                    src={embedUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <Empty
                  description={
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        No video tutorial available
                      </span>
                      <span className="text-xs text-slate-400">
                        There is no video instruction uploaded for this feature
                        yet.
                      </span>
                    </div>
                  }
                />
              )}
            </div>
          ) : error ? (
            <div className="py-16">
              <Empty description="No Content" />
            </div>
          ) : (
            content && (
              <div className="prose dark:prose-invert max-w-none">
                <MarkdownRenderer content={content} />
              </div>
            )
          )}
        </div>
      </Spin>
    </Drawer>
  );
};
